import React, { useRef } from 'react'
import { useContainerSize } from '../../hooks/useContainerSize'
import { ContainerQueryContext } from '../../context/ContainerQueryContext'
import { cn } from '@strata-monorepo/shared-ui/utils/cn'

interface ContainerRootProps {
  children: React.ReactNode
  hasProgressBar?: boolean
  className?: string
}

/**
 * Questionnaire container: establishes container query context (max 584px).
 * The outer div is the @container/qc element with NO padding so its content box can reach 584px.
 * The inner wrapper handles padding (px-6 on mobile, px-0 at @qm/qc) — it CAN query the parent qc container.
 * Children use strata:@qm/qc: (584px) and strata:@qs/qc: (400px) for container-based layout.
 */
export default function QuestionContainerRoot({
  children,
  hasProgressBar,
}: ContainerRootProps): React.ReactElement {
  // Create ref for the container element
  const containerRef = useRef<HTMLDivElement>(null)

  // Get container query state
  const containerState = useContainerSize(containerRef as React.RefObject<HTMLElement>)

  return (
    <div
      ref={containerRef}
      className='strata:@container/qc strata:max-w-[584px] strata:w-full strata:grow strata:mx-auto'
    >
      <ContainerQueryContext.Provider value={containerState}>
        <div
          className={cn(
            'strata:px-6 strata:@qm/qc:px-0 strata:my-6 strata:flex strata:flex-col strata:gap-6',
            hasProgressBar ? 'strata:@lg:my-0' : 'strata:@qm/qc:my-16',
          )}
        >
          {children}
        </div>
      </ContainerQueryContext.Provider>
    </div>
  )
}
